import { Box, Divider, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Mui from 'materials';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import DeliveryBoardIcon from 'views/delivery-board/assets/delivery-board-icon';
import { DeliveryBoardItem } from 'views/delivery-board/types/delivery-board-model.type';

const PREFIX = 'RouteCard';

const classes = {
  button: `${PREFIX}-button`,
  open: `${PREFIX}-open`,
  default: `${PREFIX}-default`,
  label: `${PREFIX}-label`,
};

type StyleProps = {
  padding: number;
  marginBottom: number;
  marginTop: number;
};

const Root = styled('div', { label: 'RouteCard' })<StyleProps>(({ theme, padding, marginBottom, marginTop }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(marginBottom),
  marginTop: theme.spacing(marginTop),
  position: 'absolute',
  width: 251,
  background: theme.palette.common.white,
  [`&.${classes.default}`]: {
    background: theme.palette.common.white,
  },
  [`& .${classes.button}`]: {
    paddingLeft: '4px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  ['.MuiTableCell-body']: {
    backgroundColor: `${theme.palette.lightBlue.o25} !important`,
  },
  [`& .MuiCollapse-wrapper`]: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 0),
    },
  },
}));

const TitleBox = styled(Box)<StyleProps>(({ theme }) => ({
  padding: theme.spacing(2, 4, 2),
  [`&.${classes.open}`]: {
    borderBottom: `1px solid ${theme.palette.bluViu.o50}50`,
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 800,
  color: theme.palette.darkBlue.main,
  textTransform: 'none',
  padding: '0 5px 0 0',
}));

type Props = {
  open?: boolean;
  count?: number;
  padding?: number;
  marginBottom?: number;
  marginTop?: number;
  elevation?: number;
  id?: string;
  sx?: SxProps<Theme> | undefined;
  fontWeight?: number;
  hiddenLabel?: boolean;
  hideBorder?: boolean;
  label?: string;
  vessel: DeliveryBoardItem | undefined;
};

const RouteCard: React.FC<Props> = ({
  label,
  open = false,
  count,
  padding = 4,
  sx,
  hideBorder = false,
  marginBottom = 2,
  marginTop = 0,
  fontWeight = 700,
  hiddenLabel,
  vessel,
  ...props
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(open);
  const PAGE_NAME_ID = 'deliveryBoard.card';

  return (
    <Root
      padding={padding}
      marginTop={marginTop}
      marginBottom={marginBottom}
      className={isOpen ? classes.open : classes.default}
      sx={{ ...sx, border: hideBorder ? 'none' : `1px solid ${theme.palette.darkBlue.main}` }}>
      <Box>
        <TitleBox padding={padding} marginBottom={0} marginTop={0} fontWeight={fontWeight}>
          <Mui.ListItemButton
            disableRipple
            className={classes.button}
            onClick={() => setIsOpen((prev) => !prev)}
            sx={{
              padding: `${theme.spacing(1, 0, 1)}`,
            }}>
            <Box width={'100%'}>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Box display={'flex'} gap={2}>
                    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} width={20}>
                      <DeliveryBoardIcon fill="#809198" stroke="#809198" />
                    </Box>
                    <CardLabel>{vessel?.vessel}</CardLabel>
                  </Box>
                </Box>
                <Box display={'flex'}>
                  {isOpen ? (
                    <ChevronUp size={24} color={theme.palette.primary.main} />
                  ) : (
                    <ChevronDown size={24} color={theme.palette.primary.main} />
                  )}
                </Box>
              </Box>
            </Box>
          </Mui.ListItemButton>
        </TitleBox>
      </Box>
      <Mui.Collapse {...props} in={isOpen}>
        <Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography color={theme.palette.common.black} fontWeight={500}>
              <FormattedMessage id={`${PAGE_NAME_ID}.contactVessel`} />
            </Typography>
          </Box>
          <Divider sx={{ background: theme.palette.darkBlue.main, mb: 3 }} orientation="horizontal" flexItem />
          <Typography color={theme.palette.common.black}>{vessel?.vesselContact?.contactPerson}</Typography>
          <Typography color={theme.palette.common.black} sx={{ whiteSpace: 'wrap', overflowWrap: 'break-word' }}>
            {vessel?.vesselContact?.email}
          </Typography>
          <Typography color={theme.palette.common.black}>{vessel?.vesselContact?.mobile}</Typography>
        </Box>
      </Mui.Collapse>
    </Root>
  );
};

export default RouteCard;
